import { UserTravelProfile, PersonalizedRecommendation } from './types/anthropic';

/**
 * Recommendation Engine
 * Advanced algorithms for generating personalized travel recommendations
 */

export class RecommendationEngine {
  /**
   * Generate personalized recommendations based on user profile and search params
   */
  static generateRecommendations(
    searchParams: any,
    profile: UserTravelProfile | null
  ): PersonalizedRecommendation[] {
    const recommendations: PersonalizedRecommendation[] = [];

    if (!profile) return recommendations;

    // Airline recommendations based on history
    const airlineRec = this.recommendAirline(searchParams, profile);
    if (airlineRec) recommendations.push(airlineRec);

    // Booking timing recommendation
    const timingRec = this.recommendBookingTiming(searchParams, profile);
    if (timingRec) recommendations.push(timingRec);

    // Destination insights
    const destinationRec = this.recommendDestinationInsights(searchParams, profile);
    if (destinationRec) recommendations.push(destinationRec);

    // Budget optimization
    const budgetRec = this.recommendBudgetOptimization(searchParams, profile);
    if (budgetRec) recommendations.push(budgetRec);

    // Cabin class recommendation
    const cabinRec = this.recommendCabinClass(searchParams, profile);
    if (cabinRec) recommendations.push(cabinRec);

    return recommendations.sort((a, b) => b.priority === 'high' ? 1 : -1);
  }

  /**
   * Recommend airline based on user history and preferences
   */
  private static recommendAirline(
    searchParams: any,
    profile: UserTravelProfile
  ): PersonalizedRecommendation | null {
    const { travelHistory, preferences } = profile;

    // Analyze airline frequency in history
    const airlineCounts = travelHistory.reduce((acc, entry) => {
      acc[entry.airline] = (acc[entry.airline] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const topAirlines = Object.entries(airlineCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);

    if (topAirlines.length === 0 && preferences.preferredAirlines.length === 0) {
      return null;
    }

    const recommendedAirlines = topAirlines.length > 0 
      ? topAirlines.map(([airline]) => airline)
      : preferences.preferredAirlines;

    return {
      type: 'flight',
      priority: 'high',
      title: 'Preferred Airlines Available',
      description: `Based on your travel history, consider flying with ${recommendedAirlines.slice(0, 2).join(' or ')}. You've had positive experiences with these airlines.`,
      reasoning: `You've flown with ${recommendedAirlines[0]} ${topAirlines[0]?.[1] || 'multiple'} times before.`,
      confidence: 0.85,
    };
  }

  /**
   * Recommend optimal booking timing
   */
  private static recommendBookingTiming(
    searchParams: any,
    profile: UserTravelProfile
  ): PersonalizedRecommendation | null {
    const { bookingPatterns } = profile;
    const { departDate } = searchParams;

    if (!departDate || bookingPatterns.averageAdvanceBookingDays === 0) {
      return null;
    }

    const daysUntilDeparture = Math.floor(
      (new Date(departDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );

    const avgBookingDays = bookingPatterns.averageAdvanceBookingDays;
    let priority: 'high' | 'medium' | 'low' = 'medium';
    let message = '';

    if (daysUntilDeparture < avgBookingDays * 0.5) {
      priority = 'high';
      message = `You typically book ${avgBookingDays} days in advance, but this flight is only ${daysUntilDeparture} days away. Book soon to secure better prices!`;
    } else if (daysUntilDeparture > avgBookingDays * 1.5) {
      priority = 'low';
      message = `You're booking earlier than usual (${daysUntilDeparture} days vs. your average ${avgBookingDays} days). You have time to monitor prices for better deals.`;
    } else {
      message = `Your booking timing is optimal based on your usual pattern of ${avgBookingDays} days in advance.`;
    }

    return {
      type: 'alert',
      priority,
      title: 'Booking Timing Insight',
      description: message,
      reasoning: `Based on your average advance booking of ${avgBookingDays} days.`,
      confidence: 0.75,
    };
  }

  /**
   * Recommend destination insights based on history
   */
  private static recommendDestinationInsights(
    searchParams: any,
    profile: UserTravelProfile
  ): PersonalizedRecommendation | null {
    const { to } = searchParams;
    const { travelHistory, bookingPatterns } = profile;

    // Check if user has been to this destination before
    const previousVisits = travelHistory.filter(
      entry => entry.destination.toLowerCase().includes(to.toLowerCase())
    );

    if (previousVisits.length > 0) {
      const lastVisit = previousVisits[previousVisits.length - 1];
      return {
        type: 'itinerary',
        priority: 'medium',
        title: 'Returning to a Favorite Destination',
        description: `You visited ${to} before in ${new Date(lastVisit.departureDate).toLocaleDateString()}. Consider exploring new areas or revisiting your favorite spots!`,
        reasoning: `Found ${previousVisits.length} previous visit(s) to this destination.`,
        confidence: 0.9,
      };
    }

    // Check for similar destinations
    const frequentDestinations = bookingPatterns.frequentDestinations.slice(0, 3);
    if (frequentDestinations.length > 0) {
      return {
        type: 'tip',
        priority: 'low',
        title: 'New Destination Adventure',
        description: `This is a new destination for you! You usually travel to ${frequentDestinations[0].destination}. Expect a different experience and plan accordingly.`,
        reasoning: `No previous visits found; comparing to your frequent destinations.`,
        confidence: 0.65,
      };
    }

    return null;
  }

  /**
   * Recommend budget optimization strategies
   */
  private static recommendBudgetOptimization(
    searchParams: any,
    profile: UserTravelProfile
  ): PersonalizedRecommendation | null {
    const { preferences } = profile;
    const { budgetRange } = preferences;

    if (budgetRange.max === 0) return null;

    return {
      type: 'tip',
      priority: 'medium',
      title: 'Budget-Friendly Tips',
      description: `Based on your budget range of ${budgetRange.currency} ${budgetRange.min}-${budgetRange.max}, consider booking mid-week flights and comparing prices across multiple airlines.`,
      reasoning: `Optimizing for your preferred budget range.`,
      confidence: 0.7,
    };
  }

  /**
   * Recommend cabin class based on preferences and history
   */
  private static recommendCabinClass(
    searchParams: any,
    profile: UserTravelProfile
  ): PersonalizedRecommendation | null {
    const { travelHistory, preferences } = profile;
    const { cabinClass } = searchParams;

    // Analyze cabin class frequency
    const cabinCounts = travelHistory.reduce((acc, entry) => {
      acc[entry.cabinClass] = (acc[entry.cabinClass] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const mostUsedCabin = Object.entries(cabinCounts)
      .sort((a, b) => b[1] - a[1])[0]?.[0];

    if (!mostUsedCabin || mostUsedCabin === cabinClass) return null;

    return {
      type: 'flight',
      priority: 'low',
      title: 'Cabin Class Suggestion',
      description: `You typically fly ${mostUsedCabin} class. Consider upgrading or maintaining your preferred comfort level.`,
      reasoning: `${mostUsedCabin} is your most frequently booked cabin class.`,
      confidence: 0.6,
    };
  }

  /**
   * Calculate personalization score (0-100)
   */
  static calculatePersonalizationScore(profile: UserTravelProfile | null): number {
    if (!profile) return 0;

    let score = 0;

    // Travel history weight: 40 points
    const historyScore = Math.min(profile.travelHistory.length * 5, 40);
    score += historyScore;

    // Preferences weight: 30 points
    const preferencesSet = [
      profile.preferences.preferredAirlines.length > 0,
      profile.preferences.preferredCabinClass.length > 0,
      profile.preferences.budgetRange.max > 0,
      profile.preferences.travelPurpose.length > 0,
    ].filter(Boolean).length;
    score += preferencesSet * 7.5;

    // Booking patterns weight: 30 points
    const patternsSet = [
      profile.bookingPatterns.averageAdvanceBookingDays > 0,
      profile.bookingPatterns.frequentDestinations.length > 0,
      profile.bookingPatterns.preferredTravelMonths.length > 0,
      profile.bookingPatterns.averageTripDuration > 0,
    ].filter(Boolean).length;
    score += patternsSet * 7.5;

    return Math.min(Math.round(score), 100);
  }
}
